import React, { useEffect } from "react";
import { useUser } from "../../providers/UserProvider";

const capitalizeWords = str => (str || "")
  .split(" ")
  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(" ");

const normalizeSlug = slug => (slug || "").replace(/^@/, "");

const nameFromSlug = (userSlug) => {
  const normalizedSlug = normalizeSlug(userSlug);
  if (!normalizedSlug) {
    return "";
  }

  const slugNameArray = normalizedSlug.split("-").filter(Boolean);
  if (/^\d+$/.test(slugNameArray[slugNameArray.length - 1])) {
    slugNameArray.pop();
  }

  return slugNameArray.join(" ");
};

const parseUserData = userData => {
  try {
    return JSON.parse(userData || "{}") || {};
  } catch (_error) {
    return {};
  }
};

const userAttribute = (user = {}, attributes = []) => {
  const attribute = attributes.find(key => user[key]);
  return attribute ? user[attribute] : undefined;
};

const profileAvatarUrl = avatar => {
  if (!avatar) { return undefined; }

  const url = avatar.replace("http://", "https://");
  if (!url.includes("my.archdaily.net")) { return url; }

  try {
    const baseUrl = new URL(url.replace("https://my.archdaily.net/", "https://snoopy.archdaily.com/myarchdaily/"));
    baseUrl.searchParams.append("format", "webp");
    baseUrl.searchParams.append("width", "200");
    return baseUrl.toString();
  } catch (_error) {
    return url;
  }
};

const getEditProfileLink = (site) => {
  const kennethMetaTag = document.querySelector('meta[name="kenneth-url"]');
  const kennethUrl = kennethMetaTag.getAttribute(`content-${site}`);

  return `${kennethUrl}/users/profile`;
}

const ProfileShell = ({ activeTab, tabs = [], children, userData }) => {
  const { site, user, userSlug } = useUser();
  const currentUserSlug = normalizeSlug(document.querySelector("meta[name='ad-user-slug']")?.content);
  const normalizedUserSlug = normalizeSlug(userSlug);
  const isProfileOwner = normalizedUserSlug && normalizedUserSlug === currentUserSlug;
  const shouldRenderProfileCard = !isProfileOwner || !!user;
  const shouldRenderTabs = isProfileOwner;
  const publicProfileUser = parseUserData(userData);
  const profileUser = isProfileOwner && user ? Object.assign({}, publicProfileUser, user) : publicProfileUser;
  const fullName = capitalizeWords(
    userAttribute(profileUser, ["full_name", "kth_full_name"]) || nameFromSlug(userSlug)
  );
  const ocupation = userAttribute(profileUser, ["ocupation", "kth_ocupation"]);
  const avatar = profileAvatarUrl(userAttribute(profileUser, ["avatar", "kth_avatar"]));
  const link = isProfileOwner ? getEditProfileLink(site) : undefined;
  const kennethId = userAttribute(profileUser, ["kenneth_id"]);
  const isPrivateProfile = userAttribute(profileUser, ["private_profile", "kth_private_profile"]);

  useEffect(() => {
    if (window.Notifications && window.Notifications.setupButtons) {
      window.Notifications.setupButtons();
    }
  }, [kennethId]);

  return (
    <>
      {shouldRenderProfileCard && (
        <div className="myad-profile-shell-card-wrapper">
          <hd-profile-card
            name={fullName}
            subtitle={ocupation}
            avatar={avatar}
            link={link}
          >
            {!isProfileOwner && !isPrivateProfile && (
              <a
                className="afd-btn afd-btn--medium afd-btn--follow myad-profile-shell-follow-button"
                data-follow-id={kennethId}
                data-follow-type="user"
                slot="action"
              >
                {window.I18n ? window.I18n.t("nether.loader_msg", { defaultValue: "•••" }) : "•••"}
              </a>
            )}
          </hd-profile-card>
        </div>
      )}

      {shouldRenderTabs && (
        <div className="myad-subscription-shell__tabs">
          <hd-link-tabs>
            {tabs.map(tab => (
              <hd-link-tab
                key={tab.id}
                href={tab.href}
                label={tab.label}
                icon={tab.icon || undefined}
                active={activeTab === tab.id ? true : undefined}
              />
            ))}
          </hd-link-tabs>
        </div>
      )}

      {children}
    </>
  );
};

export default ProfileShell;
